/* ============================================================
   bundle-sticky-cart.js — Web Component <bundle-sticky-cart>
   Presentational sibling of <bundle-section>. Owns only:
     - Visibility (IntersectionObserver on main CTA, mirroring
       assets/pdp-sticky-cart.js lines 124-142)
     - Local delivery dropdown open/close behaviour

   Slot rendering, subscribe sync and add-to-cart submission are
   driven by BundleSection (assets/bundle.js) which writes directly
   into the [data-bundle-sticky-*] hooks declared in the markup.
   ============================================================ */

class BundleStickyCart extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.removeAttribute('hidden');

    this._mainCta = document.querySelector('bundle-section [data-bundle-add-to-cart]');
    this._mobileRevealOffset = Number.parseInt(this.dataset.revealOffsetMobile || '120', 10) || 120;
    this._initVisibilityObserver();
    this._initDeliveryDropdown();
    this._initDetailsToggle();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._outsideHandler) {
      document.removeEventListener('click', this._outsideHandler);
      this._outsideHandler = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
  }

  _initVisibilityObserver() {
    if (!this._mainCta) {
      // No CTA found yet — keep the bar hidden, the section is incomplete.
      return;
    }

    const evaluate = () => {
      const rect = this._mainCta.getBoundingClientRect();
      // Show the sticky once the main CTA has scrolled past the top of
      // the viewport. On mobile, reveal it a bit earlier (offset from
      // the top edge) so users don't lose the CTA for a noticeable gap.
      // The same condition is checked from both the IO
      // callback and the scroll fallback, so a missed IO fire (which
      // can happen during fast scrolls or layout shifts) is recovered.
      const revealOffset = window.matchMedia('(max-width: 768px)').matches
        ? this._mobileRevealOffset
        : 0;
      const aboveViewport = rect.bottom <= revealOffset;
      this.classList.toggle('is-visible', aboveViewport);
    };

    if (typeof IntersectionObserver === 'function') {
      this._observer = new IntersectionObserver(
        () => evaluate(),
        { threshold: 0 }
      );
      this._observer.observe(this._mainCta);
    }

    // Scroll fallback — IntersectionObserver can miss state changes on
    // mobile when the page lays out incrementally or when JS scrolls
    // programmatically. A throttled scroll listener keeps the visibility
    // in sync in those cases. Both paths converge in `evaluate()` so
    // the toggled state stays consistent.
    let ticking = false;
    this._scrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        evaluate();
        ticking = false;
      });
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    evaluate();
  }

  // Details toggle — the "[ See details ] / [ Hide details ]" button expands
  // and collapses the mirrored status + progress block. Collapsed by default
  // (Figma node 2817:19036) so the sticky bar stays compact until requested.
  _initDetailsToggle() {
    const toggle = this.querySelector('[data-bundle-sticky-details-toggle]');
    const details = this.querySelector('[data-bundle-sticky-details]');
    if (!toggle || !details) return;

    const labelEl = toggle.querySelector('[data-bundle-sticky-details-toggle-text]') || toggle;

    /** @param {boolean} expanded */
    const setState = (expanded) => {
      // .is-open drives the grid-rows + opacity + margin transition (CSS).
      details.classList.toggle('is-open', expanded);
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      labelEl.textContent = expanded ? 'Hide Details' : 'See Details';
    };

    setState(false);

    toggle.addEventListener('click', () => {
      setState(!details.classList.contains('is-open'));
    });
  }

  _initDeliveryDropdown() {
    const select = this.querySelector('[data-bundle-sticky-delivery-select]');
    if (!select) return;

    const trigger = select.querySelector('[data-bundle-sticky-delivery-trigger]');
    const list = select.querySelector('[data-bundle-sticky-delivery-options]');
    if (!trigger || !list) return;

    const open = () => {
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      select.classList.add('bundle-sticky-cart__delivery-select--open');
    };

    const close = () => {
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      select.classList.remove('bundle-sticky-cart__delivery-select--open');
    };

    list.hidden = true;

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      if (trigger.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    this._outsideHandler = (event) => {
      if (!select.contains(event.target)) close();
    };
    document.addEventListener('click', this._outsideHandler);
  }
}

if (!customElements.get('bundle-sticky-cart')) {
  customElements.define('bundle-sticky-cart', BundleStickyCart);
}
